import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { Check } from "lucide-react";
import { m, useReducedMotion } from "framer-motion";
import { BrandWordmark } from "./BrandWordmark";
import { cn } from "../lib/cn";
import { fadeUp, staggerContainer } from "../lib/motion";
import skyLoopMp4 from "../assets/auth/sky-loop.mp4";
import skyLoopWebm from "../assets/auth/sky-loop.webm";
import skyLoopPoster from "../assets/auth/sky-loop-poster.webp";

const POINTS = [
  "No account needed for caregivers",
  "Revoke or expire any link instantly",
  "Private, unguessable share links",
];

/** The panel is display:none below `lg`; matches Tailwind's lg breakpoint. */
const PANEL_QUERY = "(min-width: 1024px)";

/**
 * The clip is square, so on most panels it scales to the panel's height and
 * the scene keeps its place between the logo and the copy. Panels wider than
 * they are tall crop it top and bottom instead; 35% keeps the scene clear of
 * both there too.
 */
const MEDIA_CLASS = "absolute inset-0 h-full w-full object-cover object-[50%_35%]";

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const list = window.matchMedia(query);
    const update = () => setMatches(list.matches);
    update();
    list.addEventListener("change", update);
    return () => list.removeEventListener("change", update);
  }, [query]);
  return matches;
}

/**
 * The storybook sky (sun, clouds, balloons, rainbow, stars) as a silent
 * looping shot made with Higgsfield: GPT Image 2.5 drew the frame in the
 * landing page's clay style, and Seedance 2.5 filmed a camera rising out of
 * the clouds onto it. The loop plays that shot back down into the clouds and
 * up out again, easing to a stop inside them, so it never jumps. The still
 * underneath is the loop's first frame: it shows while the clip loads, and is
 * all reduced motion gets. Mounted only while the panel is visible, so phones
 * never download the clip.
 */
function SkyLoop() {
  const visible = useMediaQuery(PANEL_QUERY);
  const reduce = useReducedMotion();

  if (!visible) return null;
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <img src={skyLoopPoster} alt="" className={MEDIA_CLASS} />
      {!reduce && <LoopVideo />}
      {/* Deepens the sky under the copy so it stays legible over the clip. */}
      <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-indigo-950/40 via-indigo-950/10 to-transparent" />
    </div>
  );
}

/**
 * Fades in over the still once it is actually playing, so a slow or refused
 * load never flashes an empty frame.
 */
function LoopVideo() {
  const video = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const el = video.current;
    if (!el) return;
    // React sets `muted` as a property, not an attribute; make sure autoplay
    // policies see it before asking to play.
    el.muted = true;
    el.play().catch(() => {
      /* autoplay refused (e.g. Low Power Mode) — the still frame stays */
    });
  }, []);

  return (
    <video
      ref={video}
      className={cn(MEDIA_CLASS, "transition-opacity duration-700", playing ? "opacity-100" : "opacity-0")}
      autoPlay
      muted
      loop
      playsInline
      onPlaying={() => setPlaying(true)}
    >
      <source src={skyLoopWebm} type="video/webm" />
      <source src={skyLoopMp4} type="video/mp4" />
    </video>
  );
}

/**
 * Decorative left panel for the split-screen auth pages. On short screens
 * (≤700px tall) the copy tightens so it never runs into the scene.
 */
export function AuthHero() {
  return (
    <div className="relative hidden overflow-hidden bg-gradient-to-br from-sky-500 to-indigo-700 p-10 text-white lg:flex lg:flex-col xl:p-12 [@media(max-height:700px)]:py-8">
      <SkyLoop />

      <Link
        href="/"
        className="relative inline-flex w-fit items-center gap-2.5 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
      >
        <BrandWordmark tone="light" className="h-7" />
      </Link>

      {/* The copy rises line by line once the page lands, under the sky. */}
      <m.div
        className="relative mt-auto pt-10"
        variants={staggerContainer}
        initial="hidden"
        animate="show"
      >
        <m.h2
          variants={fadeUp}
          className="font-display text-3xl font-bold leading-tight [@media(max-height:700px)]:text-2xl"
        >
          Everything your sitter needs, in one secure link.
        </m.h2>
        <m.p variants={fadeUp} className="mt-3 max-w-sm text-sky-100">
          Keep your child's care info in one place — and share it safely, only
          when you choose.
        </m.p>
        <m.ul
          variants={staggerContainer}
          className="mt-6 space-y-2.5 text-sm text-sky-50 [@media(max-height:700px)]:mt-4"
        >
          {POINTS.map((p) => (
            <m.li key={p} variants={fadeUp} className="flex items-center gap-2.5">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/20">
                <Check className="h-3 w-3" aria-hidden="true" />
              </span>
              {p}
            </m.li>
          ))}
        </m.ul>
      </m.div>

      <p className="relative mt-8 text-xs text-sky-200 [@media(max-height:700px)]:mt-5">
        Trusted care, shared safely.
      </p>
    </div>
  );
}
